<template>
  <div :class="{ 'w-full': fullWidth }">
    <CText v-if="askForPrice" :variant="textVariant">{{ t('price.on_request') }}</CText>
    <div v-else-if="priceFailed" class="flex gap-1.5 items-center">
      <CIcon name="warning" class="text-red-500" />
      <CText v-if="!onlyIcon" :variant="textVariant">{{ t('cannot_show_price') }}</CText>
    </div>
    <div v-else :class="{ 'flex items-center': isPriceTypeCampaign && !showDetailedPreviousPrice }">
      <div v-if="isPriceTypeCampaign && preCampaignPrice" class="text-gray-500">
        <CText v-if="showDetailedPreviousPrice" variant="xs" class="text-gray-500 mb-1.5"
          >{{ t('price.ordinary_price') }}: {{ formatPrice(Number(preCampaignPrice)) }}{{ displayUnit }}
          {{ vatText }}
        </CText>
        <CText v-else variant="xs" class="line-through mr-1.5"
          ><span class="font-bold">{{ formatPrice(Number(preCampaignPrice)) }}</span
          >{{ displayUnit }}</CText
        >
      </div>
      <div class="flex items-center justify-between">
        <CText
          el="p"
          :variant="textVariant"
          class="text-gray-800"
          :class="{
            'text-red-500': isPriceTypeCampaign,
          }"
        >
          {{ priceString.toLocaleLowerCase() }}
        </CText>
        <span
          v-if="displayInfoIcon"
          class="flex items-center"
          tabindex="0"
          @click.prevent="displayInfobox"
          @keyup.enter="displayInfobox"
        >
          <CIcon name="info" class="cursor-pointer" />
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { type CTextVariant } from '@/types/ui';
import { useBrandStore } from '@/stores';
import type { WebSalesUnit } from '@/types/products';

type Props = {
  price?: string;
  priceUnit?: string;
  vatText?: string;
  onlyIcon?: boolean;
  priceType?: string;
  preCampaignPrice?: string;
  showDetailedPreviousPrice?: boolean;
  displayInfoIcon?: boolean;
  askForPrice?: boolean;
  salesUnits?: WebSalesUnit;
  textVariant?: CTextVariant;
  fullWidth?: boolean;
};

const { brandConfig } = useBrandStore();
const additions = brandConfig.additions;

const emit = defineEmits<{
  (e: 'display-infobox'): void;
}>();

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
  price: '',
  priceUnit: '',
  vatText: '',
  onlyIcon: false,
  priceType: '',
  preCampaignPrice: '',
  showDetailedPreviousPrice: false,
  displayInfoIcon: false,
  askForPrice: false,
  salesUnits: () => ({}),
  textVariant: 'system-xs',
  fullWidth: false,
});

const useConversionUnit = computed(
  () => !!additions.preferComparePrice && !!props.salesUnits?.conversionUnit && !!props.salesUnits?.conversionFactor,
);
const displayUnit = computed(() =>
  useConversionUnit.value ? `/${props.salesUnits?.conversionUnit}` : `/${props.priceUnit}`,
);
const priceString = computed(() => {
  const price = useConversionUnit.value ? props.salesUnits.conversionFactor * Number(props.price) : Number(props.price);
  if (props.priceUnit) {
    return `${formatPrice(price)}${displayUnit.value} ${props.vatText}`;
  }
  return `${formatPrice(price)} ${props.vatText}`;
});
const priceFailed = computed(() => !props.price);
const isPriceTypeCampaign = computed(() => props.priceType === 'campaign');

const displayInfobox = () => emit('display-infobox');
</script>

<script lang="ts">
export default defineComponent({
  name: 'PriceIndicator',
});
</script>
